// FBS API Service

import axios, { AxiosInstance } from 'axios';
import { Customer } from './domain/domain.types';
import { getAppToken, getVendorId } from '../sessions';


interface FBSOrder {
  ediSlipRef: number;
  ediSlipStatus: number;
  erpSlipRef: number;
  erpSlipDate: string;
  erpSlipNr: string;
  erpClientCode: string;
  erpClientName: string;
  erpSlipTotal: number;
  erpSlipWeight: number;
  arpLanguage: string;
  arpShipInfoAdr: string;
}

interface FBSResponse {
  resCode: number;
  resMessage: string;
  dataset: FBSOrder[];
}

interface TokenValidationRequest {
  serviceId: string;
  vendorId: string;
  userRef: number;
  productCode: string;
  lang: string;
  apiToken: string;
}

interface TokenValidationResponse {
  resCode: number;
  resMessage: string;
  data?: any;
}

class FBSApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: 'https://api.fbsshop.de',
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  getToken(): string {
    return getAppToken() || '';
  }

  async validateToken(vendorId: string, userRef: number, apiToken: string): Promise<TokenValidationResponse> {
    try {
      const requestBody: TokenValidationRequest = {
        serviceId: '5252',
        vendorId: vendorId,
        userRef: userRef,
        productCode: '10',
        lang: 'en',
        apiToken: apiToken
      };

      console.log('🔐 Validating token with FBS API:', { ...requestBody, apiToken: apiToken.substring(0, 10) + '...' });

      const response = await this.api.post<TokenValidationResponse>('', requestBody);

      console.log('✅ Token validation response:', response.data);

      if (response.data.resCode === 200) {
        console.log('✅ Token validated successfully');
        return response.data;
      } else {
        throw new Error(`Token validation failed: ${response.data.resMessage}`);
      }
    } catch (error: any) {
      console.error('❌ Token validation error:', error);
      throw new Error(error.response?.data?.resMessage || error.message || 'Token validation failed');
    }
  }

  async fetchCustomers(startDate?: string, endDate?: string): Promise<Customer[]> {
    try {
      const token = this.getToken();
      const vendorId = getVendorId() || '10126002';

      if (!token) {
        throw new Error('No authentication token found. Please login first.');
      }

      const requestBody: any = {
        serviceId: '5211', // Order list service
        vendorId: vendorId,
        token: token,
        moduleNr: 2,
        direction: 2
      };

      // Add date range if provided
      if (startDate && endDate) {
        requestBody.beginDate = startDate;
        requestBody.endDate = endDate;
        console.log(`📅 Fetching orders from ${startDate} to ${endDate}`);
      } else {
        // Default to today if no dates provided
        const today = new Date().toISOString().split('T')[0];
        requestBody.beginDate = today;
        requestBody.endDate = today;
        console.log(`📅 Fetching orders for today: ${today}`);
      }

      const response = await this.api.post<any>('', requestBody);

      console.log('🔍 FBS API Response:', response.data);

      // Check if response is successful
      if (response.data.resCode !== 200) {
        throw new Error(`FBS API Error: ${response.data.resMessage}`);
      }

      // FBS API returns data in 'dataset' field
      let customerData: any[] = [];

      if (response.data?.dataset && Array.isArray(response.data.dataset)) {
        customerData = response.data.dataset;
        console.log('✅ Found data in response.dataset');
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        customerData = response.data.data;
        console.log('✅ Found data in response.data');
      } else {
        console.error('❌ FBS API dataset is not an array:', response.data);
        throw new Error('FBS API returned invalid dataset format');
      }

      if (customerData.length === 0) {
        console.warn('⚠️ No customers found in FBS API response');
        return [];
      }

      // Debug: Log first order from API
      console.log('🔍 Sample FBS order data:', customerData[0]);

      // Convert FBS orders to Customer format
      const customers: Customer[] = customerData.map((order: any, index: number) => {
        // FBS API returns orders with arpShipInfoAdr (delivery address)
        const fullAddress = order.arpShipInfoAdr || '';

        return {
          id: order.erpSlipRef?.toString() || `order_${index}`,
          name: order.erpClientName || order.erpClientCode || 'Unknown',
          address: fullAddress,
          lat: 0, // Will be geocoded
          lon: 0, // Will be geocoded
          location_type: 'customer' as const,
          demand: order.erpSlipWeight || 0, // Weight as demand
          selected: false,
          salesPerson: order.erpClientCode || '',
          companyNumber: 1,
          salesPersonnel: 'X',
          description: `Sipariş: ${order.erpSlipNr || ''} - Tutar: ${order.erpSlipTotal || 0} - Tarih: ${order.erpSlipDate || ''}`
        };
      });

      console.log(`✅ ${customers.length} customers loaded from FBS API`);
      return customers;
    } catch (error: any) {
      console.error('❌ FBS API Error:', error);
      throw new Error(error.response?.data?.resMessage || error.message || 'Failed to fetch customers');
    }
  }
}

export const fbsApiService = new FBSApiService();
